import type { AcquisitionPeriod, AcquisitionPeriodStatus } from './vacation.types';

const ISO_DATE_LENGTH = 10;

function isTimestampLike(value: unknown): value is { toDate: () => Date } {
    return (
        typeof value === 'object' &&
        value !== null &&
        typeof (value as { toDate?: unknown }).toDate === 'function'
    );
}

function toIsoDate(date: Date): string {
    if (Number.isNaN(date.getTime())) return '';

    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
}

export function toIsoDateOrNull(value: unknown): string | null {
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return null;
        return trimmed.slice(0, ISO_DATE_LENGTH);
    }

    if (isTimestampLike(value)) return toIsoDate(value.toDate()) || null;
    if (value instanceof Date) return toIsoDate(value) || null;

    return null;
}

export function toFiniteNumber(value: unknown, fallback = 0): number {
    const parsed = typeof value === 'string' ? Number(value) : value;
    return typeof parsed === 'number' && Number.isFinite(parsed) ? parsed : fallback;
}

const PERIOD_STATUSES: readonly AcquisitionPeriodStatus[] = [
    'Accruing',
    'Available',
    'Expiring',
    'PastLimit',
    'Expired',
    'Closed',
];

function toPeriodStatus(value: unknown): AcquisitionPeriodStatus {
    return PERIOD_STATUSES.includes(value as AcquisitionPeriodStatus)
        ? (value as AcquisitionPeriodStatus)
        : 'Accruing';
}

export function normalizeAcquisitionPeriod(raw: unknown): AcquisitionPeriod | null {
    if (typeof raw !== 'object' || raw === null) return null;

    const data = raw as Record<string, unknown>;

    return {
        id: String(data.id ?? ''),
        startDate: toIsoDateOrNull(data.startDate) ?? '',
        endDate: toIsoDateOrNull(data.endDate) ?? '',
        concessiveDeadline: toIsoDateOrNull(data.concessiveDeadline) ?? '',
        legalDeadline: toIsoDateOrNull(data.legalDeadline) ?? '',
        entitledDays: toFiniteNumber(data.entitledDays),
        takenDays: toFiniteNumber(data.takenDays),
        soldDays: toFiniteNumber(data.soldDays),
        scheduledDays: toFiniteNumber(data.scheduledDays),
        pendingDays: toFiniteNumber(data.pendingDays, 0),
        pendingSoldDays: toFiniteNumber(data.pendingSoldDays, 0),
        balanceDays: toFiniteNumber(data.balanceDays),
        status: toPeriodStatus(data.status),
    };
}

export function normalizeAcquisitionPeriods(raw: unknown): AcquisitionPeriod[] {
    if (!Array.isArray(raw)) return [];

    return raw
        .map(normalizeAcquisitionPeriod)
        .filter((period): period is AcquisitionPeriod => period !== null);
}
